import React, {Fragment} from 'react';

export interface Translations {
  [key: string]: string | Translations;
}

const get = (obj: Translations, path: string) => {
  return path.split('.').reduce<Translations | string | undefined>((acc, v) => {
    if (typeof acc === 'object') {
      return acc[v];
    }

    return undefined;
  }, obj);
};

export function createTranslator(translations: Translations) {
  return function t(key: string, defaultValue = '') {
    const translation = get(translations, key);

    if (typeof translation !== 'string') {
      console.warn(`Translation "${key}" not found`);
      return defaultValue;
    }

    return translation;
  };
}

const SELECT_TAGS = /(<\w+>.*?<\/\w+>|<\w+\s?\/>)/;
const PARSE_TAG_WITH_CONTENT = /<(\w+)>(.*?)<\/\w+>/;
const PARSE_TAG_WITHOUT_CONTENT = /<(\w+)\s?\/>/;

/**
 * Nested replacements are not supported, but are not necessarily required.
 * For example, the string
 * _`There are <b><count /></b> unread messages`_
 * can just be
 * _`There are <count /> unread messages`_
 * where the _`count`_ replacement returns _`<b>5</b>`_
 */
export function interpolate(
  str: string,
  replacements: Record<string, (content: string) => React.ReactNode>,
) {
  return str
    .split(SELECT_TAGS)
    .map((part, index) => {
      const parsedTag =
        PARSE_TAG_WITH_CONTENT.exec(part) ||
        PARSE_TAG_WITHOUT_CONTENT.exec(part);

      if (parsedTag) {
        const [_, tagName, content = ''] = parsedTag;

        if (tagName in replacements) {
          const element = replacements[tagName](content);
          if (!React.isValidElement(element)) {
            return element;
          }
          const key = `element-${index}`;
          return <Fragment key={key}>{element}</Fragment>;
        }

        throw new Error(`i18n-interpolate: Replacement "${tagName}" not found`);
      }

      return part;
    })
    .filter(Boolean);
}
